import {SampleTagged} from 'decentralized/EthereumEvents.js';
import {web3, web3WSS} from './web3.js';
import {SampleYoutube} from 'decentralized/EthereumEvents.js';
import {STRING} from 'decentralized/EthereumDataTypes.js';
const metamaskInstalled = typeof window.web3 !== 'undefined';

class EthereumQuery {
    constructor(address) {
        this.address = address;
        this.distinct = false;
        this.selectStatements = [];
        this.whereClauses = [];
        this.groupBy = [];
        this.joinClauses = [];
        this.limitSize = undefined;
        this.fromBlockNumber = '0x2';
        this.toBlockNumber = undefined;
    }

    from(eventLog) {
        this.eventLog = eventLog;
        return this;
    }

    select(arg) {
        this.selectStatements.push(arg);
        return this;
    }

    // only selects distinct items in the select clause
    distinct() {
        this.distinct = true;
        return this;
    }

    groupBy(col) {
        this.groupBy.push(col);
        return this;
    }

    where(name, value) {
        this.whereClauses.push(new WhereClause(name, value));
        return this;
    }

    whereIn(name, valueList) {
        this.whereClauses.push(new WhereClause(name, undefined, valueList));
        return this;
    }

    join(event, field) {
        this.joinClauses.push(new JoinClause(event, field));
    }

    fromBlock(fromBlock) {
        this.fromBlockNumber = web3.utils.toHex(fromBlock);
        return this;
    }

    toBlock(toBlock) {
        this.toBlockNumber = web3.utils.toHex(toBlock);
        return this;
    }

    limit(limit) {
        this.limitSize = limit;
        return this;
    }

    getTopics() {
        let topics = [web3.utils.sha3(this.eventLog.event)];

        for (let i in this.eventLog.topics) {
            let arg = this.eventLog.topics[parseInt(i)];
            let toAdd = null;
            for (let j in this.whereClauses) {
                let whereClause = this.whereClauses[parseInt(j)];
                if (whereClause.name === arg.name) {
                    let value = whereClause.value !== undefined ?
                        whereClause.value : whereClause.valueList[0];
                    toAdd = arg.convertTo(value);
                    // console.log("where %s = %s / %s", whereClause.name, value, toAdd);
                }
              }
            topics.push(toAdd);
        }
        return topics;
    }

    executeQuery() {
        return new Promise((resolve, reject) => {
            web3.eth.getPastLogs({
                address: this.address,
                topics: this.getTopics(),
                toBlock: this.toBlockNumber,
                fromBlock: this.fromBlockNumber}, (err, results) => {
                    if (err) {
                        reject(err);
                    } else {
                        let parsedResults = [];
                        results.reverse();
                        let limit = Math.min(results.length, this.limitSize || results.length);
                        for (let i=0; i < limit; i++) {
                            parsedResults.push(this.parseResult(results[i]));
                        }
                        resolve(parsedResults);
                    }
                });
        });
    }

    async watchQuery(callback) {
        let blockNumber = await web3.eth.getBlockNumber();
        let web3ToUse = metamaskInstalled ? web3 : web3WSS;
        web3ToUse.eth.subscribe(
            'logs',
            {
                address: this.address,
                topics: this.getTopics(),
                fromBlock: web3.utils.toHex(blockNumber),
            },
            (err, result) => {
                if (err) {
                    console.log('error watching ');
                    console.log(err);
                }
                let ret = this.parseResult(result);
                console.log('watch query got ');
                console.log(ret);
                callback(ret);
            });
    }

    parseResult(data) {
        if (data === undefined || data === null) {
            return undefined;
        }
        let resultSet = {};

        for (let j in this.selectStatements) {
            let select = this.selectStatements[parseInt(j)];
            for (let i in this.eventLog.topics) {
                let field = data.topics[1 + parseInt(i)];
                let arg = this.eventLog.topics[parseInt(i)];
                if (arg.name === select) {
                    // convert field
                    resultSet[select] = arg.type.convertFrom(field);
                }
            }
            
            let dataIndexSoFar = 0;
            for (let fieldIndex in this.eventLog.data) {
                fieldIndex = parseInt(fieldIndex);
                let fieldData = this.eventLog.data[fieldIndex];
                
                if (fieldData !== undefined) {
                    if (select == fieldData.name) {
                        // the data field in ethereum event logs is basically a huge hex string
                        // divided into 64-length pieces, for each data point.
                        // Also, it starts with 0x, so we must skip the first 2 characters when
                        // slicing out the desired data
                        let d = data.data.slice(2 + dataIndexSoFar, 2 + dataIndexSoFar + fieldData.size);
                        d = '0x' + d;
                        
                        if (fieldData.type == STRING ) {
                            d = '0x' + data.data.slice(130 + 64*fieldIndex);
                        } 
                        resultSet[select] = fieldData.convertFrom(d);
                    }
                }
                dataIndexSoFar += fieldData.size;
                
            }
        }
        
        resultSet.blockNumber = data.blockNumber;
        return resultSet;
    }

    getCopy() {
        let query = new EthereumQuery(this.address);
        query.eventLog = this.eventLog;
        query.selectStatements = [... this.selectStatements];
        query.joinClauses = [... this.joinClauses];
        query.groupBy = [... this.groupBy];
        query.fromBlockNumber = this.fromBlockNumber;
        query.toBlockNumber = this.toBlockNumber;
        query.limitSize = this.limitSize;
        return query;
    }

    getJson() {
        return {
            address: this.address,
            eventLog: this.eventLog.event,
            selectStatements: [... this.selectStatements],
            whereClauses: [... this.whereClauses],
            joinClauses: [... this.joinClauses],
            groupBy: [... this.groupBy],
            fromBlockNumber: web3.utils.hexToNumber(this.fromBlockNumber),
            toBlockNumber: this.toBlockNumber === undefined ? undefined : web3.utils.hexToNumber(this.toBlockNumber),
            limitSize: this.limitSize,
            distinct: this.distinct,
        };
    }
}

class WhereClause {
    constructor(argName, argValue, valueList=[]) {
        this.name = argName;
        this.value = argValue;
        this.valueList = valueList;
    }
}

class JoinClause {
    constructor(event, field) {
        this.event = event;
        this.field = field;
    }
}

export default EthereumQuery;
